/**
 * PaymentSuccess Component
 * 支付成功页面 - 精美海报设计
 */

import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getPaymentBySession } from '@/services/stripe';
import { eventApi } from '@/services/api';
import html2canvas from 'html2canvas';
import { QRCodeSVG } from 'qrcode.react';

interface Payment {
  id: number;
  registration_id: number;
  event_id: number;
  user_id: string;
  user_email: string;
  user_name?: string;
  amount: number;
  currency: string;
  price_type: string;
  stripe_status: string;
  payment_method: string;
  completed_at?: string;
}

interface RegistrationData {
  id: number;
  name: string;
  email: string;
  event_id: number;
}

interface EventData {
  id: number;
  title: string;
  title_en: string;
}

export default function PaymentSuccess() {
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const posterRef = useRef<HTMLDivElement>(null);
  
  const paymentRef = useRef<Payment | null>(null);
  const registrationRef = useRef<RegistrationData | null>(null);
  const eventRef = useRef<EventData | null>(null);
  
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [renderKey, setRenderKey] = useState(0);
  
  const sessionId = searchParams.get('session_id');
  
  useEffect(() => {
    const logoImg = new Image();
    logoImg.src = '/images/gtra-logo.png';
    
    if (sessionId) {
      loadPaymentData();
    } else {
      setLoading(false);
    }
  }, [sessionId]);
  
  const loadPaymentData = async () => {
    if (!sessionId) return;
    
    try {
      setLoading(true);
      
      // 从 localStorage 获取报名信息
      const savedRegistration = localStorage.getItem('pending_registration');
      let foundName: string | null = null;
      let foundEmail: string | null = null;
      let foundEventId: number | null = null;
      let foundEventTitle: string | null = null;
      
      if (savedRegistration) {
        try {
          const savedData = JSON.parse(savedRegistration);
          foundName = savedData?.name || null;
          foundEmail = savedData?.email || null;
          foundEventId = savedData?.eventId ? parseInt(savedData.eventId, 10) : null;
          foundEventTitle = savedData?.eventTitle || null;
        } catch (e) {
          console.log('Failed to parse localStorage:', e);
        }
      }
      
      // 检测是否为测试模式
      const isTestMode = sessionId?.startsWith('test_');
      
      // 非测试模式：获取支付信息
      if (!isTestMode) {
        try {
          const paymentData = await getPaymentBySession(sessionId);
          paymentRef.current = paymentData;
          
          if (!foundName && paymentData.user_name) {
            foundName = paymentData.user_name;
            foundEmail = paymentData.user_email;
          } else if (!foundName && paymentData.user_email) {
            foundName = paymentData.user_email.split('@')[0];
            foundEmail = paymentData.user_email;
          }
          
          if (!foundEventId && paymentData.event_id) {
            foundEventId = paymentData.event_id;
          }
          if (!foundEventTitle && paymentData.event_title) {
            foundEventTitle = paymentData.event_title;
          }
        } catch (e) {
          console.log('Failed to get payment data:', e);
        }
      }
      
      // 设置报名信息
      registrationRef.current = {
        id: paymentRef.current?.registration_id || 0,
        name: foundName || 'Guest',
        email: foundEmail || '',
        event_id: foundEventId || 0,
      };
      
      // 设置活动信息
      if (foundEventTitle) {
        eventRef.current = {
          id: foundEventId || 0,
          title: foundEventTitle,
          title_en: foundEventTitle,
        };
      } else if (foundEventId && !isTestMode) {
        try {
          const eventResponse = await eventApi.get(foundEventId);
          const eventData = eventResponse.data?.data || eventResponse.data;
          if (eventData) {
            eventRef.current = eventData;
          }
        } catch (e) {
          console.log('Failed to get event data:', e);
        }
      }
      
      setRenderKey(prev => prev + 1);
    } catch (err) {
      console.error('Failed to load payment:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const userName = registrationRef.current?.name || (isZh ? '尊贵参会者' : 'Distinguished Attendee');
  const eventTitle = eventRef.current?.title || eventRef.current?.title_en || 
    (registrationRef.current?.event_id ? `Event #${registrationRef.current.event_id}` : 
    (isZh ? 'GTRA 活动' : 'GTRA Event'));
  const userEmail = registrationRef.current?.email || '';
  const attendeeId = `GTRA-${new Date().getFullYear()}-${Math.floor(100000 + Math.random() * 900000)}`;
  
  const downloadPoster = async () => {
    if (!posterRef.current) return;
    
    setDownloading(true);
    try {
      const img = posterRef.current.querySelector('img');
      if (img && !img.complete) {
        await new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = resolve;
          setTimeout(resolve, 1000);
        });
      }
      
      const canvas = await html2canvas(posterRef.current, {
        scale: 2,
        backgroundColor: '#0A2540',
        useCORS: true,
        allowTaint: false,
        logging: false,
      });
      
      const downloadUserName = registrationRef.current?.name || 'Member';
      const link = document.createElement('a');
      link.download = `GTRA-${downloadUserName}-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('Failed to download poster:', err);
    } finally {
      setDownloading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0A2540] via-[#0d3060] to-[#1B4F72] flex items-center justify-center">
        <div className="text-center text-white">
          <div className="relative w-20 h-20 mx-auto mb-6">
            <div className="absolute inset-0 border-4 border-white/20 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-transparent border-t-[#E8A838] rounded-full animate-spin"></div>
          </div>
          <p className="text-xl font-medium">{isZh ? '正在确认您的报名...' : 'Confirming your registration...'}</p>
          <p className="text-white/60 mt-2 text-sm">{isZh ? '请稍候' : 'Please wait'}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A2540] via-[#0d3060] to-[#1B4F72] py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* 顶部标题 */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-[#E8A838]/20 flex items-center justify-center">
              <i className="ri-medal-line text-2xl text-[#E8A838]"></i>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white">
              {isZh ? '恭喜！报名成功' : 'Congratulations! Registration Confirmed'}
            </h1>
          </div>
          <p className="text-white/70 text-lg">
            {isZh 
              ? '您已成功报名参加 GTRA 活动，感谢您的支持！' 
              : 'You have successfully registered for the GTRA event. Thank you for your support!'}
          </p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-8">
          {/* 左侧 - 海报预览 */}
          <div className="order-2 lg:order-1">
            <div className="bg-white rounded-3xl p-6 shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[#0A2540]">
                  {isZh ? '您的专属参会凭证' : 'Your Exclusive Event Pass'}
                </h3>
                <button
                  onClick={downloadPoster}
                  disabled={downloading}
                  className="flex items-center gap-2 px-4 py-2 bg-[#0A2540] text-white rounded-lg hover:bg-[#0A2540]/90 transition-colors disabled:opacity-50"
                >
                  {downloading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      {isZh ? '生成中...' : 'Generating...'}
                    </>
                  ) : (
                    <>
                      <i className="ri-download-2-line"></i>
                      {isZh ? '下载海报' : 'Download Poster'}
                    </>
                  )}
                </button>
              </div>
              
              {/* 海报 */}
              <div ref={posterRef} className="relative overflow-hidden rounded-2xl" style={{ background: 'linear-gradient(135deg, #0A2540 0%, #0d3060 50%, #1B4F72 100%)' }}>
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#E8A838]/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#2C7DA0]/10 rounded-full translate-y-1/2 -translate-x-1/2"></div>
                
                <div className="h-2" style={{ background: 'linear-gradient(90deg, #C9A84C, #E8A838, #F5C842, #E8A838, #C9A84C)' }}></div>
                
                <div className="relative p-8 text-center">
                  {/* Logo */}
                  <div className="flex items-center justify-center gap-3 mb-6">
                    <div
                      style={{
                        backgroundImage: 'url(/images/gtra-logo.png)',
                        backgroundSize: 'contain',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center',
                      }}
                      className="w-20 h-20"
                      role="img"
                      aria-label="GTRA"
                    />
                  </div>
                  
                  <div className="mb-6">
                    <p className="text-[#E8A838] text-sm font-semibold tracking-widest uppercase mb-2">
                      {isZh ? '官方参会凭证' : 'Official Event Pass'}
                    </p>
                    <h2 className="text-3xl font-bold text-white mb-2">
                      {isZh ? 'GTRA 活动参会证明' : 'GTRA Event Certificate'}
                    </h2>
                  </div>
                  
                  <div className="w-24 h-1 mx-auto mb-6 rounded-full" style={{ background: 'linear-gradient(90deg, transparent, #E8A838, transparent)' }}></div>
                  
                  <div className="mb-8">
                    <p className="text-white/60 text-sm mb-1">
                      {isZh ? '参会者姓名' : 'Attendee Name'}
                    </p>
                    <p className="text-3xl font-bold text-white mb-4">
                      {userName}
                    </p>
                    
                    <p className="text-white/60 text-sm mb-1">
                      {isZh ? '活动名称' : 'Event Name'}
                    </p>
                    <p className="text-xl font-semibold text-white mb-4">
                      {eventTitle}
                    </p>
                  </div>
                  
                  {paymentRef.current && (
                    <div className="inline-flex items-center gap-3 px-6 py-3 bg-white/10 rounded-2xl mb-6">
                      <i className="ri-vip-diamond-line text-[#E8A838] text-xl"></i>
                      <div className="text-left">
                        <p className="text-white/60 text-xs">{isZh ? '票种' : 'Ticket Type'}</p>
                        <p className="text-white font-semibold">
                          {paymentRef.current.price_type === 'early_bird' 
                            ? (isZh ? '早鸟票' : 'Early Bird')
                            : paymentRef.current.price_type === 'regular'
                            ? (isZh ? '普通票' : 'Regular Ticket')
                            : (isZh ? '免费票' : 'Free Pass')}
                        </p>
                      </div>
                      <div className="w-px h-8 bg-white/20"></div>
                      <div className="text-left">
                        <p className="text-white/60 text-xs">{isZh ? '支付金额' : 'Amount Paid'}</p>
                        <p className="text-white font-bold">
                          {paymentRef.current.currency === 'CNY' ? '¥' : paymentRef.current.currency === 'EUR' ? '€' : '$'}
                          {paymentRef.current.amount.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  <div className="bg-white/5 rounded-xl px-4 py-2 inline-block">
                    <p className="text-white/40 text-xs">{isZh ? '参会编号' : 'Attendee ID'}</p>
                    <p className="text-white/80 font-mono text-sm">{attendeeId}</p>
                  </div>
                  
                  <div className="mt-6 pt-6 border-t border-white/10">
                    <div className="inline-flex items-center gap-4">
                      <div className="w-16 h-16 bg-white rounded-lg p-1 flex items-center justify-center">
                        <QRCodeSVG 
                          value="https://gtraweb3.com/events" 
                          size={56}
                          level="M"
                        />
                      </div>
                      <div className="text-left">
                        <p className="text-white/60 text-xs">{isZh ? '扫码签到' : 'Scan to Check-in'}</p>
                        <p className="text-white font-semibold text-sm">GTRA Web3</p>
                        <p className="text-white/40 text-xs">gtraweb3.com/events</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="h-1" style={{ background: 'linear-gradient(90deg, #C9A84C, #E8A838, #F5C842, #E8A838, #C9A84C)' }}></div>
              </div>
              
              <div className="mt-4 text-center">
                <p className="text-gray-500 text-sm">
                  {isZh ? '保存海报并分享到社交媒体，让更多人了解 GTRA！' : 'Save the poster and share on social media to spread the word about GTRA!'}
                </p>
              </div>
            </div>
          </div>
          
          {/* 右侧 - 订单详情 */}
          <div className="order-1 lg:order-2 space-y-6">
            <div className="bg-white rounded-3xl p-8 shadow-2xl text-center">
              <div className="relative w-24 h-24 mx-auto mb-6">
                <div className="absolute inset-0 bg-green-100 rounded-full animate-ping opacity-20"></div>
                <div className="relative w-full h-full bg-green-100 rounded-full flex items-center justify-center">
                  <i className="ri-checkbox-circle-fill text-5xl text-green-500"></i>
                </div>
              </div>
              
              <h2 className="text-2xl font-bold text-[#0A2540] mb-2">
                {isZh ? '支付确认成功' : 'Payment Confirmed'}
              </h2>
              <p className="text-gray-600">
                {isZh 
                  ? '您的报名已被确认，活动详情已发送到您的邮箱。' 
                  : 'Your registration has been confirmed. Event details have been sent to your email.'}
              </p>
            </div>
            
            {paymentRef.current && (
              <div className="bg-white rounded-3xl p-6 shadow-xl">
                <h3 className="text-lg font-semibold text-[#0A2540] mb-4 flex items-center gap-2">
                  <i className="ri-file-list-3-line text-[#2C7DA0]"></i>
                  {isZh ? '订单详情' : 'Order Details'}
                </h3>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-500">{isZh ? '订单编号' : 'Order ID'}</span>
                    <span className="text-[#0A2540] font-mono text-sm">#{paymentRef.current.id}</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-500">{isZh ? '活动名称' : 'Event'}</span>
                    <span className="text-[#0A2540] font-medium text-right max-w-[60%]">{eventTitle}</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-500">{isZh ? '参会者' : 'Attendee'}</span>
                    <span className="text-[#0A2540] font-medium">{userName}</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-500">{isZh ? '支付金额' : 'Amount'}</span>
                    <span className="text-[#0A2540] font-bold text-lg">
                      {paymentRef.current.currency === 'CNY' ? '¥' : paymentRef.current.currency === 'EUR' ? '€' : '$'}
                      {paymentRef.current.amount.toFixed(2)}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-500">{isZh ? '支付方式' : 'Payment Method'}</span>
                    <span className="text-[#0A2540]">
                      {paymentRef.current.payment_method === 'stripe' ? (
                        <span className="inline-flex items-center gap-1">
                          <span className="font-bold text-[#635BFF]">S</span> Stripe
                        </span>
                      ) : (
                        paymentRef.current.payment_method?.toUpperCase() || 'N/A'
                      )}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center py-3">
                    <span className="text-gray-500">{isZh ? '订单状态' : 'Status'}</span>
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                      {isZh ? '已完成' : 'Completed'}
                    </span>
                  </div>
                </div>
              </div>
            )}
            
            <div className="bg-gradient-to-r from-[#E8A838]/10 to-[#2C7DA0]/10 rounded-3xl p-6 border border-[#E8A838]/20">
              <h3 className="text-lg font-semibold text-[#0A2540] mb-4 flex items-center gap-2">
                <i className="ri-lightbulb-line text-[#E8A838]"></i>
                {isZh ? '接下来做什么？' : 'What\'s Next?'}
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#2C7DA0] text-white flex items-center justify-center text-sm font-bold shrink-0">1</div>
                  <div>
                    <p className="text-[#0A2540] font-medium">{isZh ? '查收确认邮件' : 'Check Your Email'}</p>
                    <p className="text-gray-500 text-sm">
                      {isZh ? '活动详情和电子票已发送到您的邮箱' : 'Event details and e-ticket sent to your email'}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#2C7DA0] text-white flex items-center justify-center text-sm font-bold shrink-0">2</div>
                  <div>
                    <p className="text-[#0A2540] font-medium">{isZh ? '保存参会凭证' : 'Save Your Event Pass'}</p>
                    <p className="text-gray-500 text-sm">
                      {isZh ? '下载上方海报，活动当天出示使用' : 'Download the poster above for event day'}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#2C7DA0] text-white flex items-center justify-center text-sm font-bold shrink-0">3</div>
                  <div>
                    <p className="text-[#0A2540] font-medium">{isZh ? '关注 GTRA' : 'Follow GTRA'}</p>
                    <p className="text-gray-500 text-sm">
                      {isZh ? '获取更多活动资讯和精彩内容' : 'Stay updated with latest events'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex gap-4">
              <button
                onClick={() => navigate('/events')}
                className="flex-1 px-6 py-4 bg-white text-[#0A2540] rounded-2xl font-semibold hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 shadow-lg"
              >
                <i className="ri-calendar-event-line"></i>
                {isZh ? '浏览更多活动' : 'Browse More Events'}
              </button>
              
              <button
                onClick={() => navigate('/')}
                className="flex-1 px-6 py-4 bg-[#0A2540] text-white rounded-2xl font-semibold hover:bg-[#0A2540]/90 transition-colors flex items-center justify-center gap-2"
              >
                <i className="ri-home-4-line"></i>
                {isZh ? '返回首页' : 'Back to Home'}
              </button>
            </div>
          </div>
        </div>
        
        <div className="mt-8 text-center">
          <p className="text-white/50 text-sm">
            {isZh 
              ? '如有任何问题，请联系 support@gtraweb3.com' 
              : 'For questions, please contact support@gtraweb3.com'}
          </p>
        </div>
      </div>
    </div>
  );
}
